import React, { useState, useEffect, useCallback, useRef } from "react";
import { Alert } from "./Alert";
import { useTable, useGlobalFilter, useAsyncDebounce, useFilters, useSortBy, usePagination, useRowSelect, useReactTable, flexRender, getCoreRowModel } from 'react-table'
import { isMobile } from 'react-device-detect';
import styled from "styled-components";
import { v4 as uuidv4 } from 'uuid';
import {
  StyledTable,
  StyledThead,
  StyledTh,
  StyledTd,
  StyledTr,
  StyledTrExpanded,
  StyledPagination,
  InputContainer,
  Label,
  StyledSelect,
  StyledButton,
  SearchAndFilterContainer,
  StyledSearchInput,
  TableContainer
} from "../StyledComponents/Table/TableComponents";

export function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id },
}) {
  const options = React.useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row) => {
      options.add(row.values[id]);
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  return (
    <StyledSelect
      name={id}
      id={id}
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
    >
      <option value="">All</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </StyledSelect>
  );
}

const AnimatedRow = styled.div`
  overflow: hidden;
  transition: max-height 0.4s ease-in-out, opacity 0.4s ease-in-out;
  max-height: ${({ isExpanded }) => (isExpanded ? "1000px" : "0")};
  opacity: ${({ isExpanded }) => (isExpanded ? 1 : 0)};
`;

const ExpandedRowContainer = styled.div`
  border-radius: 4px;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
`;

const ExpandingRow = ({ isExpanded, content }) => {
  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState("0px");

  useEffect(() => {
    if (isExpanded && contentRef.current) {
      setMaxHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setMaxHeight("0px");
    }
  }, [isExpanded]);

  return (
    <AnimatedRow
      style={{ maxHeight }}
      isExpanded={isExpanded}
      ref={contentRef}
    >
      <ExpandedRowContainer>{content}</ExpandedRowContainer>
    </AnimatedRow>
  );
};

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef()
    const resolvedRef = ref || defaultRef

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate
    }, [resolvedRef, indeterminate])

    return (
      <>
        <input type="checkbox" ref={resolvedRef} {...rest} />
      </>
    )
  }
)

function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  hardCount = null,
  label = 'Search:'
}) {
  const count = (hardCount !== null ? hardCount : preGlobalFilteredRows.length)
  const [value, setValue] = React.useState(globalFilter)
  const onChange = useAsyncDebounce(value => {
    setGlobalFilter(value || undefined)
  }, 200)

  return (
    <InputContainer>
      <Label>{label}{' '}</Label>
      <StyledSearchInput
        value={value || ""}
        onChange={e => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        placeholder={`${count} records...`}
      />
    </InputContainer>
  )
}


export function ServerSidePagingTable({
  showSearch = true,
  columns,
  data,
  childrenContainerHeight = null,
  children,
  recordCount,
  pagingFn,
  filter = false,
  mobilePagingSize = 5,
  arrFilterOptions = null,
  filterLabel = null,
  setPagingTracker,
  arrAccountFilters = null,
  arrCoWorkersFilter = null,
  qryParams = null,
  barcode = false,
  expand = null,
  showSearchField = true,
  colours = null,
  info = null,
  type='info' }) {

  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(isMobile ? 5 : 10);
  const [pageCount, setPageCount] = useState(Math.ceil(recordCount / pageSize));
  const [filterStatus, setFilterStatus] = useState(qryParams?.status);
  const [account, setAccount] = useState(qryParams?.account ? qryParams.account : (arrAccountFilters && arrAccountFilters[0]?.value));
  const [coWorker, setCoWorker] = useState((arrCoWorkersFilter ? arrCoWorkersFilter[0]?.value : ''));
  const [searchValue, setSearchValue] = useState();

  const handleRowClick = (event, rowId) => {
    if (
      event.target.tagName === "BUTTON" ||
      event.target.tagName === "SELECT" ||
      event.target.closest("button") ||
      event.target.closest("select")
    ) {
      return;
    }
    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  const fetchData = useCallback(async () => {
    try {
      await pagingFn(account, currentPage + 1, pageSize, filterStatus, searchValue, coWorker);
      setPagingTracker({
        currentPage: currentPage + 1,
        pageSize: pageSize,
        filterStatus: filterStatus,
        searchValue: searchValue,
        account: account,
        coWorker: coWorker
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [currentPage, pageSize, pagingFn, filterStatus, searchValue, account, coWorker]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: currentPage, pageSize },
      manualPagination: true,
      pageCount,
    },
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    previousPage,
    nextPage,
    gotoPage,
    pageCount: tablePageCount,
    state: { pageIndex },
  } = tableInstance;

  useEffect(() => {
    setPageCount(Math.ceil(recordCount / pageSize));
  }, [recordCount, pageSize]);

  return (
    <TableContainer>
      {showSearch && (
        <>
          <SearchAndFilterContainer>
            {arrAccountFilters &&
              <InputContainer key='tblAccountFilters'>
                <Label htmlFor='tblAccountFilters'>Account: </Label>
                <StyledSelect
                  name='tblAccountFilters'
                  id='tblAccountFilters'
                  value={account}
                  onChange={(e) => setAccount(e.target.value || undefined)}
                >
                  {arrAccountFilters.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </StyledSelect>
              </InputContainer>
            }

            {barcode &&
              <InputContainer>
                <Label>EyeTicket® ID :{' '}</Label>
                <StyledSearchInput
                  value={coWorker}
                  onChange={(e) => setCoWorker(e.target.value || undefined)}
                  placeholder={`${recordCount} records...`}
                />
              </InputContainer>
            }
            {arrCoWorkersFilter &&

              <InputContainer key='tblCoWorkerFilters'>
                <Label htmlFor='tblCoWorkerFilters'>Raised By: </Label>
                <StyledSelect
                  name='tblCoWorkerFilters'
                  id='tblCoWorkerFilters'
                  value={coWorker}
                  onChange={(e) => setCoWorker(e.target.value || undefined)}
                >
                  <option key={999} value=''>
                    All coworkers..
                  </option>
                  {arrCoWorkersFilter
                    .filter(option => option.account === (account ? account : arrAccountFilters[0]?.value))
                    .map((option, index) => (
                      <option key={index} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </StyledSelect>
              </InputContainer>
            }
            {arrFilterOptions &&
              <InputContainer key='tblFilterOptions'>
                <Label htmlFor='tblFilterOptions'>{filterLabel}: </Label>
                <StyledSelect
                  name='tblFilterOptions'
                  id='tblFilterOptions'
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value || undefined)}
                >
                  <option value="">All</option>
                  {arrFilterOptions.map((option, i) => (
                    <option key={i} value={option.value}>
                      {option.name}
                    </option>
                  ))}
                </StyledSelect>
              </InputContainer>}
            {showSearchField &&
              <InputContainer>
                <Label>Search:{' '}</Label>
                <StyledSearchInput
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value || undefined)}
                  placeholder={`${recordCount} records...`}
                />
              </InputContainer>}
            <div style={{ float: "right", height: childrenContainerHeight ? childrenContainerHeight : "28px" }}>
              {children}
            </div>
          </SearchAndFilterContainer>
          {info && <Alert type={type}>{info}</Alert>}
        </>
      )}
      
        <StyledTable {...getTableProps()}>
          <StyledThead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()} key={uuidv4()}>
                {headerGroup.headers.map(column => (
                  <StyledTh {...column.getHeaderProps()} key={uuidv4()}>
                    {column.render('Header')}
                  </StyledTh>
                ))}
              </tr>
            ))}
          </StyledThead>
          <tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);

              const rowKey = uuidv4();
              const isExpanded = expandedRow === row.id;
              return (
                <React.Fragment key={rowKey}>
                  <StyledTr
                    {...row.getRowProps()}
                    onClick={(e) => {
                      if (expand) handleRowClick(e, row.id);
                    }}
                    style={{
                      cursor: expand ? "pointer" : "default",
                    }}
                  >
                    {row.cells.map((cell) => {
                      const cellKey = uuidv4();
                      return (
                        <StyledTd {...cell.getCellProps()} key={cellKey}>
                          {cell.render("Cell")}
                        </StyledTd>
                      );
                    })}
                  </StyledTr>
                  {isExpanded && expand && (
                    <StyledTr key={`expanded-${rowKey}`}>
                      <StyledTd colSpan={columns.length}>
                        <ExpandingRow
                          isExpanded={isExpanded}
                          content={typeof expand === "function" ? expand(row.original) : expand}
                        />
                      </StyledTd>
                    </StyledTr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </StyledTable>

      <StyledPagination className="pagination">
        <InputContainer>
          <Label>
          {pageCount !== 0 && `Page `}
            <strong>
            {pageCount === 0 ? `No pages available` : `${currentPage + 1} of ${pageCount}`}
            </strong>{' '}
          </Label>
          <StyledSelect
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
          >
            {[2, 5, 10, 20].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </StyledSelect>
        </InputContainer>
        <div>
          <StyledButton
            onClick={() => {
              gotoPage(0);
              setCurrentPage(0);
            }}
            disabled={!canPreviousPage}
          >
            {'<<'}
          </StyledButton>{' '}
          <StyledButton
            onClick={() => {
              previousPage();
              setCurrentPage(pageIndex - 1);
            }}
            disabled={!canPreviousPage}
          >
            {'<'}
          </StyledButton>{' '}
          <StyledButton
            onClick={() => {
              nextPage();
              setCurrentPage(pageIndex + 1);
            }}
            disabled={!canNextPage}
          >
            {'>'}
          </StyledButton>{' '}
          <StyledButton
            onClick={() => {
              gotoPage(pageCount - 1);
              setCurrentPage(pageCount - 1);
            }}
            disabled={!canNextPage}
          >
            {'>>'}
          </StyledButton>{' '}
        </div>
      </StyledPagination>
      </TableContainer>
  );
};

function Table({ columns, data, showLeftCheckbox = false, showSearch = true, mobilePageSize = 5, children, filter = false, hardCount = null, serverSidePagingFn = null, setPostCodeFilter = null, postcodeValue = null, isValidPc = null, colours = null, vhValue = null,
  info = null,
  type='info',
  overridePageLen = false,
  defaultPageSize = 5
 }) {
  const initialState = (filter ? {
    pageIndex: 0, pageSize: (isMobile ? mobilePageSize : defaultPageSize), filters: [
      {
        id: 'status',
        value: 'Open',
      },
    ]
  } : { pageIndex: 0, pageSize: (isMobile ? mobilePageSize : defaultPageSize) });

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable({
    columns,
    data,
    initialState: initialState,
  },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      hooks.visibleColumns.push(columns => [
        {
          id: 'selection',
          Header: ({ getToggleAllPageRowsSelectedProps }) => {
            return showLeftCheckbox ? (
              <div>
                <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
              </div>
            ) : null;
          },
          Cell: ({ row }) => {
            return showLeftCheckbox ? (
              <div>
                {showLeftCheckbox ? <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} /> : null}
              </div>
            ) : null;
          },
        },
        ...columns,
      ])
    }
  );

  return (
    <>
      {showSearch &&
      <>
       <SearchAndFilterContainer>
       {setPostCodeFilter && <>
          <InputContainer key={'postCodeSearch'}>
            <Label htmlFor={'postCodeSearch'}>Search By Postcode: </Label>
            <StyledSearchInput
              value={postcodeValue || ""}
              style={{ backgroundColor: isValidPc ? 'white' : '#fcf8e3' }}
              onChange={e => {
                setPostCodeFilter(e.target.value);
              }}
            />
          </InputContainer>
        </>}
        <GlobalFilter
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={state.globalFilter}
          setGlobalFilter={setGlobalFilter}
          hardCount={hardCount}
          label={"Filter Results by Name:"}
        />
        {headerGroups.map((headerGroup) =>
          headerGroup.headers.map((column) =>
            column.Filter ? (
              <InputContainer key={column.id}>
                <Label htmlFor={column.id}>{column.render("Header")}: </Label>
                {column.render("Filter")}
              </InputContainer>
            ) : null
          )
        )}
        <div style={{ width: "100%", float: "right" }}>
          {children}
        </div>
        
      </SearchAndFilterContainer>
      {info && !overridePageLen && page.length === 0 && <Alert type={type}>{info}</Alert>}
      {overridePageLen && <Alert type={type}>{info}</Alert>}
      </>
      }

      <TableContainer vhValue={vhValue}>
        <StyledTable {...getTableProps()} border="1">
          <StyledThead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => {
                  if (column.id === 'selection' && !showLeftCheckbox) {
                    return null;
                  }
                  return (
                    <StyledTh {...column.getHeaderProps(column.getSortByToggleProps())} isSelect={column.id === 'selection'} >
                      {column.render('Header')}
                      <span>
                        {column.isSorted
                          ? column.isSortedDesc
                            ? ' ▼'
                            : ' ▲'
                          : ''}
                      </span>
                    </StyledTh>
                  );
                })}
              </tr>
            ))}
          </StyledThead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <StyledTr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    if (cell.column.id === 'selection' && !showLeftCheckbox) {
                      return null;
                    }
                    return (
                      <StyledTd {...cell.getCellProps()}>
                        {cell.render("Cell")}
                      </StyledTd>
                    );
                  })}
                </StyledTr>
              );
            })}
          </tbody>
        </StyledTable>
      </TableContainer>
      <StyledPagination className="pagination">
        <InputContainer>
          <Label>
            Page{' '}
            <strong>
              {pageOptions.length === 0 ? `No pages available` : `${state.pageIndex + 1} of ${pageOptions.length}`}
            </strong>{' '}
          </Label>
          <StyledSelect
            value={state.pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value))
            }}
          >
            {[2, 5, 10, 20].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </StyledSelect>
        </InputContainer>
        <div>
          <StyledButton onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
            {'<<'}
          </StyledButton>{' '}
          <StyledButton onClick={() => previousPage()} disabled={!canPreviousPage}>
            {'<'}
          </StyledButton>{' '}
          <StyledButton onClick={() => nextPage()} disabled={!canNextPage}>
            {'>'}
          </StyledButton>{' '}
          <StyledButton onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
            {'>>'}
          </StyledButton>{' '}
        </div>
      </StyledPagination>
    </>
  );
}

export default Table;
